import { anchorLabel, type CheckinStatus, type Occurrence } from '@planer/shared';
import { Check, Flame, Minus, X } from 'lucide-react';
import { haptic } from '../lib/actions';
import { occColor } from '../lib/colors';

/**
 * "Danes še": things without a time of day (vitamini, 2 l vode, kreatin on a rest day).
 * One tap checks off; the item opens for everything else.
 */
export function AnytimeList({
  occs,
  status,
  streaks,
  past,
  onStatus,
  onOpen,
}: {
  occs: readonly Occurrence[];
  status: (o: Occurrence) => CheckinStatus | null | undefined;
  streaks: Record<string, number>;
  /** A past day: the heading says what it was, not what is left. */
  past: boolean;
  onStatus: (o: Occurrence, s: CheckinStatus | null) => void;
  onOpen: (o: Occurrence) => void;
}) {
  if (occs.length === 0) return null;
  const open = occs.filter((o) => o.requiresCheck && !status(o)).length;
  return (
    <section aria-labelledby="anytime-title" data-testid="anytime">
      <div className="mb-2 flex min-h-9 items-center justify-between px-1">
        <h2 id="anytime-title" className="section-label">
          {past ? 'Brez ure' : 'Danes še'}
        </h2>
        {!past && open > 0 && <span className="text-[12px] text-muted">{open} odprto</span>}
      </div>
      <ul className="card divide-y divide-line overflow-hidden">
        {occs.map((o) => {
          const s = status(o);
          const streak = streaks[o.itemId];
          const label = anchorLabel(o);
          const muted = s === 'done' || s === 'skipped';
          return (
            <li
              key={o.key}
              className="flex min-h-14 items-center gap-3 px-3"
              data-testid="anytime-row"
            >
              {o.requiresCheck ? (
                <button
                  type="button"
                  onClick={() => {
                    haptic(s === 'done' ? [8, 40, 8] : 12);
                    onStatus(o, s === 'done' ? null : 'done');
                  }}
                  aria-label={
                    s === 'done'
                      ? `${o.title}: prekliči opravljeno`
                      : `Označi ${o.title} kot opravljeno`
                  }
                  aria-pressed={s === 'done'}
                  className="tap grid h-11 w-11 shrink-0 place-items-center"
                >
                  <span
                    className="grid h-7 w-7 place-items-center rounded-full"
                    style={{
                      background:
                        s === 'done'
                          ? 'var(--done)'
                          : s === 'missed'
                            ? 'var(--missed)'
                            : s === 'skipped'
                              ? 'var(--skip)'
                              : 'var(--surface)',
                      boxShadow: s ? 'none' : `inset 0 0 0 2px ${occColor(o)}`,
                    }}
                  >
                    {s === 'done' && <Check size={16} strokeWidth={3.5} color="var(--on-status)" />}
                    {s === 'missed' && <X size={16} strokeWidth={3.5} color="var(--on-status)" />}
                    {s === 'skipped' && (
                      <Minus size={16} strokeWidth={3.5} color="var(--on-status)" />
                    )}
                  </span>
                </button>
              ) : (
                <span className="w-11 shrink-0" />
              )}
              <button
                type="button"
                onClick={() => {
                  onOpen(o);
                }}
                className="tap min-w-0 flex-1 py-2 text-left"
              >
                <span
                  className={`block truncate text-[16px] font-medium ${muted ? 'text-muted line-through decoration-subtle' : ''}`}
                >
                  {o.title}
                </span>
                {label && label !== 'kadarkoli' && (
                  <span className="block truncate text-[13px] text-muted">{label}</span>
                )}
              </button>
              {streak && streak >= 2 && (
                <span
                  className="inline-flex shrink-0 items-center gap-1 rounded-full bg-mid-soft px-2 py-0.5 text-[12px] font-semibold text-mid-ink tabular-nums"
                  title={`${streak} dni zapored`}
                  data-testid="streak"
                >
                  <Flame size={13} aria-hidden /> {streak}
                  <span className="sr-only"> dni zapored</span>
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
